import specialtiesData from '../data/SpecialtyOverview.json';

/**
 * Determines which training pathways (ACCS, CST, IMT, Neurosurgery, etc.)
 * are valid for a given specialty name based on the SpecialtyOverview JSON.
 * 
 * @param {string} specialtyName - The name of the specialty (e.g., "Cardiology")
 * @returns {string[]} - Array of valid pathway codes (e.g., ['IMT', 'ACCS'])
 */
export const getTrainingPathsForSpecialty = (specialtyName) => {
  try {
    // 1. Find the specialty in the data
    const specialty = specialtiesData.specialties.find(
      s => s.name === specialtyName
    );
    
    // If not found, default to IMT (safe fallback)
    if (!specialty) {
      // Robust check: if the exact name isn't found but contains "Radiology", offer it
      if (specialtyName && (specialtyName.includes("Radiology") || specialtyName.includes("Clinical radiology"))) {
         return ['Radiology'];
      }
      
      console.warn(`Specialty "${specialtyName}" not found in database`);
      return ['IMT'];
    }
    
    // 2. Get the raw text options from the JSON
    const coreOptions = specialty.additional_info.core_training_options;
    
    // Convert to array if it's a string
    const options = Array.isArray(coreOptions) ? coreOptions : [coreOptions];
    
    // 3. Define Pathway Logic
    
    const hasACCS = options.some(option => 
      option && (
        option.includes('ACCS') || 
        option.includes('Acute care common stem')
      )
    );
    
    const hasIMT = options.some(option => 
      option && (
        option.includes('IMT') || 
        option.includes('Internal medicine') || // Covers "General (internal) medicine"
        option.includes('ACCS (Internal medicine)')
      )
    );
    
    const hasCST = options.some(option => 
      option && (
        option.includes('CST') || 
        option.includes('Core Surgical Training') ||
        option.includes('Core surgery')
      )
    );

    const hasNeurosurgery = options.some(option => 
      option && (
        option.includes("Neurosurgery") || 
        option.includes("Neurosurgery ST1")
      )
    );

    const hasRadiology = options.some(option => 
      option && (
        option.includes("Radiology") || 
        option.includes("Clinical Radiology")
      )
    );

    // 4. Construct the result array
    const availablePaths = [];
    if (hasRadiology) availablePaths.push('Radiology');
    if (hasACCS) availablePaths.push('ACCS');
    if (hasIMT) availablePaths.push('IMT');
    if (hasCST) availablePaths.push('CST');
    if (hasNeurosurgery) availablePaths.push('Neurosurgery');
    
    // Default to IMT if we found the specialty but matched no known pathways
    if (availablePaths.length === 0) {
      console.warn(`No specific training path found for ${specialtyName}, defaulting to IMT`);
      return ['IMT'];
    }
    
    return availablePaths;

  } catch (error) {
    console.error('Error determining training path:', error);
    return ['IMT'];
  }
};